import React from 'react';
import { motion } from "framer-motion";
import {
    TrendingUp,
    Timer,
    CalendarClock,
    Stars,
    Building2,
    Presentation,
    CheckCircle2,
    ArrowRight,
    BookOpen,
    Layers
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Program() {
    const navigate = useNavigate();

    return (
        <section
            id="program"
            className="
                relative w-full py-24 px-4 sm:px-10 lg:px-20
                overflow-hidden font-poppins
            "
        >

            <div
                className="
                    relative z-10
                    /*
                       FIX 1: Adjusted Container Widths
                       - md:w-[90%] (was 85%): More room on tablets.
                       - lg:w-[95%] (was 75%): CRITICAL FIX. 75% was too narrow for 2 columns on iPad Pro (1024px).
                       - xl:w-[80%] (Restores original narrow look for big desktops)
                    */
                    w-[95%] md:w-[90%] lg:w-[95%] xl:w-[80%] 2xl:w-[75%]
                    mx-auto
                    /*
                       FIX 2: Grid Gap Logic
                       - lg:gap-12 (was gap-24): Reduces gap on tablets to prevent squeezing.
                       - xl:gap-24: Restores wide gap on desktops.
                    */
                    grid lg:grid-cols-2 gap-16 lg:gap-12 xl:gap-24 items-start
                "
            >

                {/* ---------------- LEFT CONTENT ---------------- */}
                <div className="space-y-8">
                    {/* Badge */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                        <span className="text-xs font-bold tracking-widest text-primary uppercase">
                            Curriculum
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-4xl md:text-5xl font-bold text-dark dark:text-light"
                    >
                        <span className="block leading-[1.2]">Our Flagship</span>
                        <span className="block leading-[1.2] text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                                Program
                            </span>
                    </motion.h2>

                    <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed border-l-4 border-primary/30 pl-6">
                        A complete transformation system for traders, investors, and professionals who want to forecast markets with <span className="font-semibold text-dark dark:text-light">scientific precision</span>.
                    </p>

                    {/* Program Highlights Grid */}
                    {/*
                        FIX 3: Stack grid on very small screens, 2 cols on mobile+.
                        This is fine as is, but ensuring gap-4 keeps it tight.
                    */}
                    <div className="grid sm:grid-cols-2 gap-4 mt-8">
                        <HighlightItem text="Trend Capturing Techniques" icon={<TrendingUp size={18} />} />
                        <HighlightItem text="Time Technique Trading™" icon={<Timer size={18} />} />
                        <HighlightItem text="Swing & Positional Forecasting" icon={<CalendarClock size={18} />} />
                        <HighlightItem text="Astro Timing Model" icon={<Stars size={18} />} />
                        <HighlightItem text="Institutional Research Tools" icon={<Building2 size={18} />} />
                        <HighlightItem text="Live Forecast Labs" icon={<Presentation size={18} />} />
                    </div>

                    <div className="pt-2">
                        <button className="group flex items-center gap-2 text-primary font-bold hover:gap-4 transition-all duration-300" onClick={() => navigate('/courses')}>
                            Explore Full Syllabus
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* ---------------- RIGHT CONTENT (MODULE CARDS) ---------------- */}
                <div className="space-y-6 w-full mt-8 lg:mt-0">

                    {/* Module 1 Card */}
                    <ModuleCard
                        title="Forex Trading Basics"
                        subtitle="Foundation Module"
                        icon={<BookOpen className="w-6 h-6 text-white" />}
                        delay={0.2}
                    >
                        <ul className="space-y-3">
                            <ListItem text="Understand global market operations & currency movements." />
                            <ListItem text="Master market sessions, volume timings & ICT Kill Zones." />
                            <ListItem text="Risk management, PIPs, Lot sizes & P/L calculation." />
                            <ListItem text="Develop a professional trading psychology." />
                        </ul>
                    </ModuleCard>

                    {/* Module 2 Card */}
                    <ModuleCard
                        title="Time Techniques Mastery"
                        subtitle="Advanced Module"
                        icon={<Layers className="w-6 h-6 text-white" />}
                        delay={0.4}
                        isPrimary={true}
                    >
                        <ul className="space-y-3">
                            <ListItem text="High-impact intraday strategies & momentum rules." isLight={true} />
                            <ListItem text="18+ time cycle models & planetary influence decoding." isLight={true} />
                            <ListItem text="Precision frameworks: 1-hour price square & timing candles." isLight={true} />
                            <ListItem text="Applied numerical logic for Indices, Crypto & Commodities." isLight={true} />
                        </ul>
                    </ModuleCard>

                </div>
            </div>
        </section>
    );
}

/* ------------------ SUB-COMPONENTS ------------------ */

function HighlightItem({ text, icon }) {
    return (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-white dark:bg-bg-dark-2 border border-gray-100 dark:border-white/5 shadow-sm">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                {icon}
            </div>
            <span className="text-sm font-medium text-dark dark:text-light">{text}</span>
        </div>
    );
}

function ModuleCard({ title, subtitle, icon, children, delay, isPrimary = false }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay }}
            className={`
                relative p-6 sm:p-8 rounded-3xl overflow-hidden
                ${isPrimary
                ? "bg-gradient-to-br from-primary to-[#004e80] text-white shadow-xl shadow-primary/20"
                : "bg-white dark:bg-bg-dark-2 border border-gray-200 dark:border-white/10 shadow-lg"
            }
            `}
        >
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <div className={`
                    w-12 h-12 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0
                    ${isPrimary ? "bg-white/20 backdrop-blur-md" : "bg-gradient-to-br from-primary to-secondary"}
                `}>
                    {icon}
                </div>
                <div>
                    <span className={`text-xs font-bold uppercase tracking-wider ${isPrimary ? "text-blue-100" : "text-primary"}`}>
                        {subtitle}
                    </span>
                    <h3 className={`text-xl font-bold ${isPrimary ? "text-white" : "text-dark dark:text-light"}`}>
                        {title}
                    </h3>
                </div>
            </div>

            {/* Content */}
            <div className={`text-sm leading-relaxed ${isPrimary ? "text-blue-50" : "text-gray-600 dark:text-gray-300"}`}>
                {children}
            </div>
        </motion.div>
    );
}

function ListItem({ text, isLight = false }) {
    return (
        <li className="flex items-start gap-3">
            <CheckCircle2
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isLight ? "text-blue-200" : "text-primary"}`}
            />
            <span>{text}</span>
        </li>
    );
}